import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

import {
  AVAAIO_LANGUAGES,
  AvaaioLanguageCode,
  getInitialPreferredLanguage,
  normalizeAvaaioLanguage,
  persistPreferredLanguage,
} from "../utils/avaaioLanguage";
import {
  getAvaaioUiCopy,
} from "../utils/avaaioUiCopy";

const API_BASE =
  import.meta.env.VITE_API_BASE ||
  "https://api.avaaio.com";

function resolveAuthToken(): string {
  const tokenKeys = [
    "ava_session_token",
    "ava_token",
    "token",
    "access_token",
    "authToken",
    "jwt",
  ];

  for (const key of tokenKeys) {
    const localToken =
      window.localStorage.getItem(key)?.trim();

    if (localToken) {
      return localToken;
    }

    const sessionToken =
      window.sessionStorage.getItem(key)?.trim();

    if (sessionToken) {
      return sessionToken;
    }
  }

  return "";
}

function sessionExpiredMessage(
  language: AvaaioLanguageCode
): string {
  const messages: Partial<
    Record<AvaaioLanguageCode, string>
  > = {
    en: "Your session has expired. Please sign in again.",
    es: "Tu sesión ha caducado. Inicia sesión nuevamente.",
    fr: "Votre session a expiré. Veuillez vous reconnecter.",
    de: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
    it: "La sessione è scaduta. Accedi di nuovo.",
    pt: "Sua sessão expirou. Entre novamente.",
  };

  return (
    messages[language] ||
    messages.en ||
    "Your session has expired. Please sign in again."
  );
}

export default function Language() {
  const navigate = useNavigate();
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);

  const [selected, setSelected] =
    useState<AvaaioLanguageCode>(
      getInitialPreferredLanguage()
    );

  const [saved, setSaved] =
    useState<AvaaioLanguageCode>(
      getInitialPreferredLanguage()
    );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const interfaceCopy =
    getAvaaioUiCopy(selected);

  const selectedOption = useMemo(
    () =>
      AVAAIO_LANGUAGES.find(
        (item) => item.code === selected
      ) || AVAAIO_LANGUAGES[0],
    [selected]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadPreference() {
      try {
        const token = resolveAuthToken();

        const response = await fetch(
          `${API_BASE}/api/v1/auth/me`,
          {
            credentials: "include",
            headers: {
              ...(token
                ? {
                    Authorization:
                      `Bearer ${token}`,
                  }
                : {}),
            },
          }
        );

        const payload = await response
          .json()
          .catch(() => ({}));

        if (!response.ok || cancelled) {
          return;
        }

        const user = payload?.user || payload;
        const language = normalizeAvaaioLanguage(
          user?.preferred_language
        );

        setSelected(language);
        setSaved(language);

        persistPreferredLanguage(language);
      } catch {
        // The stored local preference remains usable.
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadPreference();

    return () => {
      cancelled = true;
    };
  }, []);

  async function saveLanguage() {
    if (saving) return;

    setSaving(true);
    setMessage("");

    try {
      const token = resolveAuthToken();

      const response = await fetch(
        `${API_BASE}/api/v1/auth/profile`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token
              ? {
                  Authorization:
                    `Bearer ${token}`,
                }
              : {}),
          },
          body: JSON.stringify({
            preferred_language: selected,
          }),
        }
      );

      const payload = await response
        .json()
        .catch(() => ({}));

      if (
        response.status === 401 ||
        payload?.error === "missing_token" ||
        payload?.detail === "No token provided"
      ) {
        throw new Error(
          sessionExpiredMessage(selected)
        );
      }

      if (!response.ok || payload?.ok === false) {
        throw new Error(
          payload?.detail ||
          payload?.error ||
          interfaceCopy.language.error
        );
      }

      const language =
          persistPreferredLanguage(selected);

        setSaved(language);
        setMessage(
          interfaceCopy.language.success
        );

        window.setTimeout(() => {
          navigate(
            "/settings",
            { replace: true }
          );
        }, 350);
    } catch (error: any) {
      setMessage(
        error?.message ||
        interfaceCopy.language.error
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.eyebrow}>
          {interfaceCopy.language.eyebrow}
        </div>

        <h1 style={styles.title}>
          {interfaceCopy.language.title}
        </h1>

                <p style={styles.subtitle}>
          {interfaceCopy.language.subtitle}
        </p>

        <div style={styles.card}>
          <label
            htmlFor="avaaio-account-language"
            style={styles.label}
          >
            {interfaceCopy.language.preferred}
          </label>

          <select
            id="avaaio-account-language"
            value={selected}
            disabled={loading || saving}
            onChange={(event) => {
              setSelected(
                normalizeAvaaioLanguage(
                  event.target.value
                )
              );
              setMessage("");
            }}
            style={styles.select}
          >
            {AVAAIO_LANGUAGES.map((language) => (
              <option
                key={language.code}
                value={language.code}
                style={styles.option}
              >
                {language.nativeLabel}
              </option>
            ))}
          </select>

          <div style={styles.selection}>
            {interfaceCopy.language.selected}: {selectedOption.nativeLabel}
          </div>

                    <div style={styles.note}>
            {interfaceCopy.language.note}
          </div>

          <button
            type="button"
            onClick={saveLanguage}
            disabled={
              loading ||
              saving ||
              selected === saved
            }
            style={{
              ...styles.saveButton,
              opacity:
                loading ||
                saving ||
                selected === saved
                  ? 0.56
                  : 1,
              cursor:
                loading ||
                saving ||
                selected === saved
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {saving
              ? interfaceCopy.language.saving
              : selected === saved
              ? interfaceCopy.language.saved
              : interfaceCopy.language.save}
          </button>

          {message ? (
            <div style={styles.message}>
              {message}
            </div>
          ) : null}
        </div>

      </div>
    </div>
  );
}

function makeStyles(themeColors: any): Record<string, React.CSSProperties> {
  return {
  page: {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    padding: "24px 16px 32px",
    color: themeColors.text,
    background: themeColors.bg,
    fontFamily:
      'Inter, "Segoe UI", Arial, sans-serif',
  },

  shell: {
    width: "min(100%, 760px)",
    margin: "0 auto",
  },

  eyebrow: {
    color: themeColors.accent2,
    fontSize: "0.76rem",
    fontWeight: 800,
    letterSpacing: "0.22em",
    textAlign: "center",
    marginBottom: "10px",
  },

  title: {
    margin: "0 0 10px",
    textAlign: "center",
    fontSize: "clamp(2rem, 7vw, 3rem)",
    lineHeight: 1.08,
  },

  subtitle: {
    margin: "0 auto 22px",
    maxWidth: "650px",
    textAlign: "center",
    color: themeColors.mutedText,
    lineHeight: 1.65,
  },

  card: {
    borderRadius: "24px",
    padding: "24px",
    background: themeColors.panel,
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.glow,
  },

  label: {
    display: "block",
    marginBottom: "9px",
    color: themeColors.text,
    fontWeight: 800,
  },

  select: {
    width: "100%",
    minHeight: "56px",
    boxSizing: "border-box",
    borderRadius: "16px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    color: themeColors.text,
    WebkitTextFillColor: themeColors.text,
    colorScheme: "dark",
    padding: "0 16px",
    fontSize: "1rem",
    fontWeight: 700,
    outline: "none",
  },

  option: {
    background: themeColors.panel,
    color: themeColors.text,
  },

  selection: {
    marginTop: "12px",
    color: themeColors.accent2,
    fontSize: "0.9rem",
    fontWeight: 700,
  },

  note: {
    marginTop: "18px",
    padding: "14px",
    borderRadius: "15px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.cardBg,
    color: themeColors.mutedText,
    fontSize: "0.86rem",
    lineHeight: 1.65,
  },

  saveButton: {
    width: "100%",
    minHeight: "54px",
    marginTop: "18px",
    borderRadius: "16px",
    border: `1px solid ${themeColors.border}`,
    background: `linear-gradient(90deg, ${themeColors.accent}, ${themeColors.accent2})`,
    color: themeColors.bg,
    fontSize: "1rem",
    fontWeight: 900,
  },

  message: {
    marginTop: "14px",
    textAlign: "center",
    color: themeColors.accent2,
    fontWeight: 700,
    lineHeight: 1.5,
  },

  backButton: {
    width: "100%",
    minHeight: "50px",
    marginTop: "16px",
    borderRadius: "15px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.text,
    fontWeight: 800,
    cursor: "pointer",
  },
  };
}
